import { useCallback, useMemo, useState } from "react";
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { AuthError } from "@azure/msal-browser";
import { Header } from "./components/Header";
import { SourcePicker } from "./components/SourcePicker";
import { FileList, type ListedFile } from "./components/FileList";
import { ProgressView, type FileProgress } from "./components/ProgressView";
import { ResultView } from "./components/ResultView";
import { TitleImagePicker, type TitleSelections } from "./components/TitleImagePicker";
import { ImageCropGrid, type GridFile } from "./components/ImageCropGrid";
import { useGraphToken } from "./auth/useGraphToken";
import { useIdToken } from "./auth/useIdToken";
import { graphLoginRequest } from "./auth/msalConfig";
import { isM365Configured } from "./auth/authMode";
import {
  createGraphClient,
  resolveSharePointLink,
  listImagesInFolder,
  ensureScaledSubfolder as ensureSpScaledSubfolder,
  downloadFile,
  uploadScaledFile,
  type DriveFolderRef,
  type RemoteFile,
} from "./sharepoint/graphClient";
import {
  pickLocalFolder,
  ensureScaledSubfolder as ensureLocalScaledSubfolder,
  scanLocalImages,
  readLocalFile,
  writeScaledFile,
  type LocalFolderRef,
  type LocalFile,
  type FolderDiagnostics,
} from "./local/fileSystemAccess";
import { WorkerPool, type JobResult } from "./processing/workerPool";
import { DEFAULT_OPTIONS, MAX_FILE_BYTES, type CropRect } from "./processing/types";
import { readJpegExif, attachExifAndDpi, exifOverheadBytes } from "./processing/metadata";
import { logUsage } from "./telemetry/logUsage";
import { resolveOutputNames } from "./naming/roomNames";
import { useRoomSuggestions, type RoomSuggestionState } from "./naming/useRoomSuggestions";
import { AdminDashboard } from "./stats/AdminDashboard";
import type { GetIdToken } from "./stats/api";

type Mode = "sharepoint" | "local" | "single";

type AppState =
  | { phase: "idle"; banner?: string }
  | { phase: "loading"; message: string }
  | { phase: "ready"; mode: Mode; folderName: string; files: ListedFile[]; raw: SourceContext }
  | { phase: "processing"; folderName: string; progress: FileProgress[] }
  | {
      phase: "done";
      successCount: number;
      errors: Array<{ fileName: string; message: string }>;
      durationSeconds: number;
      downloaded: boolean;
    };

interface SourceContext {
  mode: Mode;
  spFolder?: DriveFolderRef;
  spScaledFolder?: DriveFolderRef;
  spFiles?: RemoteFile[];
  localFolder?: LocalFolderRef;
  localScaled?: FileSystemDirectoryHandle;
  localFiles?: LocalFile[];
  // Einzelbild-Modus: einzelne Dateien ohne Ordner. Ergebnis wird heruntergeladen.
  singleFiles?: File[];
}

// Die Statistik liegt unter /admin (Zugriff prüft der Server, nicht diese Weiche).
const isAdminRoute = typeof window !== "undefined" && window.location.pathname.replace(/\/+$/, "") === "/admin";

export function App() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {isM365Configured ? <M365App /> : <LocalOnlyApp />}
      </main>
      <footer className="text-center text-xs text-kampmeyer-neutral-500 py-4">
        KAMPMEYER Immobilien · Interne Anwendung · Build {__APP_BUILD__}
      </footer>
    </div>
  );
}

function M365App() {
  return (
    <>
      <AuthenticatedTemplate>
        {isAdminRoute ? <AdminWithMsal /> : <SkalierungApp sharePointEnabled={true} />}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
    </>
  );
}

const DEV_TOKEN = async () => "dev";

function AdminWithMsal() {
  return <AdminDashboard getIdToken={useIdToken()} />;
}

function LocalOnlyApp() {
  if (isAdminRoute) {
    // Lokale Entwicklung ohne Azure: Platzhalter-Token für den Test-Server.
    if (import.meta.env.DEV) return <AdminDashboard getIdToken={DEV_TOKEN} />;
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="card text-sm">Die Statistik ist nur mit Microsoft-365-Anmeldung verfügbar.</div>
      </div>
    );
  }
  return <SkalierungApp sharePointEnabled={false} />;
}

function LoginScreen() {
  const { instance } = useMsal();
  const signIn = () => void instance.loginRedirect(graphLoginRequest);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "#1b365d" }}
    >
      <div className="w-full text-center" style={{ maxWidth: 378 }}>
        <svg
          viewBox="0 0 270.28 28.42"
          width="232"
          height="24"
          fill="#fff"
          role="img"
          aria-label="KAMPMEYER"
          style={{ display: "block", margin: "0 auto 26px" }}
        >
          <path d="M264.37,19.14c1.65-.86,2.9-2.07,3.77-3.62.86-1.55,1.3-3.4,1.3-5.53,0-3.16-1.03-5.61-3.1-7.35-2.07-1.74-4.97-2.61-8.73-2.61h-12.76v28.39h7.41v-7.86h5.51l4.13,7.86h8.38l-5.91-9.28ZM261.15,13.51c-.82.76-1.99,1.13-3.5,1.13h-5.39V5.94h5.39c1.51,0,2.68.37,3.5,1.11.82.74,1.24,1.8,1.24,3.18s-.41,2.52-1.24,3.28ZM217.82,28.42h22.8v-5.91h-15.39v-5.35l13.53-.04v-5.87h-13.53v-5.31h14.99V.03h-22.4v28.39ZM208.51.03l-6.56,12.19L195.23.03h-7.41l10.45,19.52v8.87h7.41v-9.07L215.92.03h-7.41ZM163.78,28.42h22.8v-5.91h-15.39v-5.35l13.53-.04v-5.87h-13.53v-5.31h14.99V.03h-22.4v28.39ZM126.15,28.42h6.6V10.44l7.29,15.15h4.78l7.25-15.15.04,17.98h6.56V.03h-8.06l-8.18,16.6L134.21.03h-8.06v28.39ZM120.25,2.64C118.26.9,115.45.03,111.83.03h-12.47v28.39h7.41v-7.86h5.06c3.62,0,6.42-.92,8.42-2.77,2-1.85,3-4.45,3-7.8,0-3.16-1-5.61-3-7.35ZM114.97,13.51c-.82.76-1.99,1.13-3.5,1.13h-4.7V5.94h4.7c1.51,0,2.68.37,3.5,1.11.82.74,1.23,1.8,1.23,3.18s-.41,2.52-1.23,3.28ZM61.73,28.42h6.6V10.44l7.29,15.15h4.78l7.25-15.15.04,17.98h6.56V.03h-8.06l-8.18,16.6L69.79.03h-8.06v28.39ZM47.91.03h-7.61l-12.19,28.39h7.61l1.98-5.02h12.27l1.94,5.02h7.9L47.91.03ZM39.93,17.73l3.97-10.12,3.89,10.12h-7.86ZM18.08,14.24l-4.9,5.45,5.67,8.73h8.5l-9.2-14.17h-.06ZM0,0v28.42h7.44v-7.14L26.58.03h-8.5L7.44,11.83V0H0Z" />
        </svg>
        <p style={{ fontWeight: 700, fontSize: 20, letterSpacing: "-0.01em", color: "#fff", margin: 0 }}>
          Bildskalierung
        </p>
        <p style={{ fontSize: 12.5, color: "rgba(255,255,255,.72)", margin: "6px 0 26px" }}>
          Interner Zugriff · KAMPMEYER Immobilien
        </p>
        <div className="bg-white dark:bg-[#182130]" style={{ borderRadius: 6, padding: "24px 22px" }}>
          <p
            className="text-[#55534d] dark:text-[#9a978f]"
            style={{ fontSize: 13, lineHeight: 1.5, margin: "0 0 18px" }}
          >
            Melde dich mit deinem Microsoft-365-Konto an, um deine SharePoint-Ordner zu nutzen.
          </p>
          <button
            onClick={signIn}
            className="w-full"
            style={{
              fontSize: 14,
              fontWeight: 500,
              padding: 11,
              background: "#004680",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
            }}
          >
            <span style={{ display: "inline-grid", gridTemplateColumns: "9px 9px", gap: 2 }}>
              <span style={{ width: 9, height: 9, background: "#f25022" }} />
              <span style={{ width: 9, height: 9, background: "#7fba00" }} />
              <span style={{ width: 9, height: 9, background: "#00a4ef" }} />
              <span style={{ width: 9, height: 9, background: "#ffb900" }} />
            </span>
            Mit Microsoft 365 anmelden
          </button>
        </div>
        <p style={{ fontSize: 11, color: "rgba(255,255,255,.6)", lineHeight: 1.6, margin: "20px 0 0" }}>
          Anmeldung über dein KAMPMEYER-Microsoft-365-Konto. Es werden keine Passwörter auf dieser
          Seite gespeichert.
        </p>
      </div>
    </div>
  );
}

function SkalierungApp({ sharePointEnabled }: { sharePointEnabled: boolean }) {
  // useMsal/useGraphToken dürfen nur aufgerufen werden, wenn der MsalProvider
  // gemounted ist (also nur im M365-Modus). Wir rendern daher zwei Varianten.
  if (sharePointEnabled) return <SkalierungAppWithMsal />;
  // Lokal-Modus: keine Statistik/KI. In der Entwicklung Platzhalter-Token für den Test-Server.
  return <SkalierungAppCore sharePointEnabled={false} getToken={null} getIdToken={import.meta.env.DEV ? DEV_TOKEN : null} />;
}

function SkalierungAppWithMsal() {
  const getToken = useGraphToken();
  const getIdToken = useIdToken();
  return <SkalierungAppCore sharePointEnabled={true} getToken={getToken} getIdToken={getIdToken} />;
}

interface CoreProps {
  sharePointEnabled: boolean;
  getToken: (() => Promise<string>) | null;
  // Für die Nutzungsstatistik (null = Lokal-Modus ohne Anmeldung, nichts wird erfasst).
  getIdToken: GetIdToken | null;
}

function SkalierungAppCore({ sharePointEnabled, getToken, getIdToken }: CoreProps) {
  const [state, setState] = useState<AppState>({ phase: "idle" });
  const [titles, setTitles] = useState<TitleSelections>({ primary: null, alternative: null });
  const [titlesBusy, setTitlesBusy] = useState(false);
  // Manuell angepasste 4:3-Ausschnitte je Bild (normalisiert). Nicht enthaltene
  // Bilder werden mittig auf 4:3 zugeschnitten.
  const [imageCrops, setImageCrops] = useState<Record<string, CropRect>>({});
  // Eingegebene Dateinamen je Bild (leer = Originalname). Später füllt die KI das vor.
  const [imageNames, setImageNames] = useState<Record<string, string>>({});
  const setImageName = useCallback(
    (file: string, value: string) => setImageNames((prev) => ({ ...prev, [file]: value })),
    [],
  );
  // KI-Vorschläge füllen Felder vor, die noch niemand angefasst hat (Eingaben haben Vorrang).
  const rooms = useRoomSuggestions(getIdToken);
  const effectiveNames = useMemo(
    () => ({ ...rooms.state.suggestions, ...imageNames }),
    [rooms.state.suggestions, imageNames],
  );
  // Endgültige Dateinamen (ohne Endung): Raumname bzw. Originalname, Dubletten durchnummeriert.
  const finalNames = useMemo(
    () => (state.phase === "ready" ? resolveOutputNames(state.files.map((f) => f.name), effectiveNames) : {}),
    [state, effectiveNames],
  );
  const [diag, setDiag] = useState<FolderDiagnostics | null>(null);

  const reset = () => {
    setState({ phase: "idle" });
    setTitles({ primary: null, alternative: null });
    setTitlesBusy(false);
    setImageCrops({});
    setImageNames({});
    rooms.reset();
    setDiag(null);
  };

  const handleSharePoint = async (url: string) => {
    if (!sharePointEnabled || !getToken) return;
    rooms.reset();
    setState({ phase: "loading", message: "Lade SharePoint-Ordner…" });
    try {
      const client = createGraphClient(getToken);
      const folder = await resolveSharePointLink(client, url);
      const scaled = await ensureSpScaledSubfolder(client, folder);
      const files = await listImagesInFolder(client, folder);
      setState({
        phase: "ready",
        mode: "sharepoint",
        folderName: folder.name,
        files: files.map((f) => ({ name: f.name, size: f.size })),
        raw: { mode: "sharepoint", spFolder: folder, spScaledFolder: scaled, spFiles: files },
      });
    } catch (err) {
      setState({ phase: "idle", banner: humanError(err) });
    }
  };

  const handleLocal = async () => {
    rooms.reset();
    setState({ phase: "loading", message: "Lese lokalen Ordner…" });
    try {
      const folder = await pickLocalFolder();
      const scaled = await ensureLocalScaledSubfolder(folder);
      const { files, diagnostics } = await scanLocalImages(folder);
      setDiag(diagnostics);
      setState({
        phase: "ready",
        mode: "local",
        folderName: folder.name,
        files: files.map((f) => ({ name: f.name, size: f.size })),
        raw: { mode: "local", localFolder: folder, localScaled: scaled, localFiles: files },
      });
    } catch (err) {
      // Abbrechen des Datei-Dialogs ist kein Fehler – ohne rote Meldung zurück.
      if (err instanceof DOMException && err.name === "AbortError") {
        setState({ phase: "idle" });
        return;
      }
      setState({ phase: "idle", banner: humanError(err) });
    }
  };

  const handleSingle = (files: File[]) => {
    const imgs = files.filter((f) => /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name));
    if (imgs.length === 0) {
      setState({ phase: "idle", banner: "Bitte ein Bild wählen (JPG, PNG, WebP oder HEIC)." });
      return;
    }
    rooms.reset();
    setState({
      phase: "ready",
      mode: "single",
      folderName: imgs.length === 1 ? imgs[0].name : `${imgs.length} Einzelbilder`,
      files: imgs.map((f) => ({ name: f.name, size: f.size })),
      raw: { mode: "single", singleFiles: imgs },
    });
  };

  const handleStart = async () => {
    if (state.phase !== "ready") return;
    const ctx = state.raw;
    const fileList = state.files;
    const folderName = state.folderName;
    const start = performance.now();

    // Titelbild-Varianten (Haupt- + optionales Alternativbild) als zusätzliche Crop-Jobs.
    type TitleExport = { fileName: string; bytes: ArrayBuffer; crop: CropRect; outputName: string };
    const titleExports: TitleExport[] = [];
    if (titles.primary) {
      const p = titles.primary;
      titleExports.push({ fileName: p.fileName, bytes: p.bytes, crop: p.crop4x3, outputName: "Titelbild_4-3.jpg" });
      titleExports.push({ fileName: p.fileName, bytes: p.bytes, crop: p.crop3x4, outputName: "Titelbild_3-4.jpg" });
    }
    if (titles.alternative) {
      const a = titles.alternative;
      titleExports.push({ fileName: a.fileName, bytes: a.bytes, crop: a.crop4x3, outputName: "Titelbild_Alternativ_4-3.jpg" });
      titleExports.push({ fileName: a.fileName, bytes: a.bytes, crop: a.crop3x4, outputName: "Titelbild_Alternativ_3-4.jpg" });
    }

    const progress: FileProgress[] = [
      ...fileList.map((f): FileProgress => ({ name: f.name, status: "pending" })),
      ...titleExports.map((v): FileProgress => ({ name: v.outputName, status: "pending" })),
    ];
    setState({ phase: "processing", folderName, progress: [...progress] });

    const updateProgress = (name: string, patch: Partial<FileProgress>) => {
      const idx = progress.findIndex((p) => p.name === name);
      if (idx >= 0) {
        progress[idx] = { ...progress[idx], ...patch };
        setState({ phase: "processing", folderName, progress: [...progress] });
      }
    };

    const pool = new WorkerPool(DEFAULT_OPTIONS, 3);
    const errors: Array<{ fileName: string; message: string }> = [];
    let successCount = 0;
    let titleCount = 0;
    let inputBytes = 0;
    let outputBytes = 0;

    try {
      const tasks = fileList.map(async (entry) => {
        updateProgress(entry.name, { status: "processing" });
        try {
          const sourceBytes = await loadSourceBytes(ctx, entry.name);
          inputBytes += sourceBytes.byteLength;

          // EXIF (inkl. Orientation) im Main-Thread extrahieren, damit es nach
          // dem Worker-Encoding wieder eingespielt werden kann. Orientation
          // wird bewusst NICHT physisch angewandt — das Bild bleibt in der
          // Original-Ausrichtung, der Tag bleibt erhalten.
          const isJpeg = /\.jpe?g$/i.test(entry.name);
          const { exifDict } = isJpeg ? readJpegExif(sourceBytes) : { exifDict: null };

          const jobResult: JobResult = await pool.process({
            id: entry.name,
            fileName: entry.name,
            data: sourceBytes,
            crop: imageCrops[entry.name],
            cropAspect: 4 / 3,
            maxBytes: jpegBudget(exifDict),
            outputName: finalNames[entry.name] ? `${finalNames[entry.name]}.jpg` : undefined,
          });
          if (!jobResult.ok) {
            errors.push({ fileName: entry.name, message: jobResult.message });
            updateProgress(entry.name, { status: "error", errorMessage: jobResult.message });
            return;
          }

          // EXIF + DPI im Main-Thread auf den encodierten JPEG-Output mergen
          const finalBytes = attachExifAndDpi(jobResult.result.bytes, exifDict, DEFAULT_OPTIONS.dpi);

          await writeOutputBytes(ctx, jobResult.result.outputName, finalBytes);
          outputBytes += finalBytes.byteLength;
          successCount += 1;
          updateProgress(entry.name, { status: "done" });
        } catch (err) {
          const message = humanError(err);
          errors.push({ fileName: entry.name, message });
          updateProgress(entry.name, { status: "error", errorMessage: message });
        }
      });
      const titleTasks = titleExports.map((ex) =>
        (async () => {
          updateProgress(ex.outputName, { status: "processing" });
          try {
            // EXIF aus einer Kopie lesen (Original-Bytes im State bleiben unangetastet).
            const { exifDict } = readJpegExif(ex.bytes.slice(0));
            const jobResult: JobResult = await pool.process({
              id: ex.outputName,
              fileName: ex.fileName,
              data: ex.bytes.slice(0), // eigene Kopie pro Job (Transfer detacht den Buffer)
              crop: ex.crop,
              outputName: ex.outputName,
              maxBytes: jpegBudget(exifDict),
            });
            if (!jobResult.ok) {
              errors.push({ fileName: ex.outputName, message: jobResult.message });
              updateProgress(ex.outputName, { status: "error", errorMessage: jobResult.message });
              return;
            }
            const finalBytes = attachExifAndDpi(jobResult.result.bytes, exifDict, DEFAULT_OPTIONS.dpi);
            await writeOutputBytes(ctx, jobResult.result.outputName, finalBytes);
            outputBytes += finalBytes.byteLength;
            successCount += 1;
            titleCount += 1;
            updateProgress(ex.outputName, { status: "done" });
          } catch (err) {
            const message = humanError(err);
            errors.push({ fileName: ex.outputName, message });
            updateProgress(ex.outputName, { status: "error", errorMessage: message });
          }
        })(),
      );

      await Promise.all([...tasks, ...titleTasks]);
    } finally {
      pool.terminate();
    }

    const durationSeconds = (performance.now() - start) / 1000;

    void logUsage(getIdToken, {
      sourceType: ctx.mode,
      objectName: folderName,
      images: successCount,
      titleImages: titleCount,
      errors: errors.length,
      inputBytes,
      outputBytes,
      durationSeconds,
    });

    setState({ phase: "done", successCount, errors, durationSeconds, downloaded: ctx.mode === "single" });
  };

  async function loadSourceBytes(ctx: SourceContext, fileName: string): Promise<ArrayBuffer> {
    if (ctx.mode === "single") {
      const file = ctx.singleFiles!.find((f) => f.name === fileName);
      if (!file) throw new Error("Datei nicht gefunden");
      return await file.arrayBuffer();
    }
    if (ctx.mode === "sharepoint") {
      const file = ctx.spFiles!.find((f) => f.name === fileName);
      if (!file) throw new Error("Datei nicht in der SharePoint-Liste");
      return await downloadFile(getToken!, file);
    }
    const file = ctx.localFiles!.find((f) => f.name === fileName);
    if (!file) throw new Error("Datei nicht in der lokalen Liste");
    return await readLocalFile(file);
  }

  async function writeOutputBytes(
    ctx: SourceContext,
    outputName: string,
    bytes: Uint8Array,
  ): Promise<void> {
    // Kopie in einen "echten" ArrayBuffer (nicht ArrayBufferLike) für die Upload-API
    const arr = new Uint8Array(bytes.byteLength);
    arr.set(bytes);
    const buffer = arr.buffer;
    if (ctx.mode === "single") {
      downloadBytes(outputName, buffer);
      return;
    }
    if (ctx.mode === "sharepoint" && getToken) {
      await uploadScaledFile(getToken, ctx.spScaledFolder!, outputName, buffer);
      return;
    }
    await writeScaledFile(ctx.localScaled!, outputName, buffer);
  }

  // Einheitliche Galerie-Dateien für beide Quellen. Lokal: direkt aus dem
  // FileSystemFileHandle. SharePoint: Download der vor-authentifizierten URL
  // (dieselbe Datei wird beim Skalieren erneut geladen – für Konsistenz mit lokal).
  const gridFiles: GridFile[] = useMemo(() => {
    if (state.phase !== "ready") return [];
    if (state.raw.mode === "single") {
      return state.raw.singleFiles!.map((f) => ({ name: f.name, getFile: async () => f }));
    }
    if (state.raw.mode === "sharepoint") {
      return state.raw.spFiles!.map((f) => ({
        name: f.name,
        getFile: async () => new Blob([await downloadFile(getToken!, f)]),
      }));
    }
    return state.raw.localFiles!.map((f) => ({
      name: f.name,
      getFile: () => f.handle.getFile(),
    }));
    // getToken ist über die Session stabil; bewusst nicht in den Deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      {state.phase === "idle" && (
        <>
          {state.banner && (
            <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3">
              <span>{state.banner}</span>
              {state.banner === AUTH_MESSAGE && getToken && (
                <button
                  className="btn-primary !px-4 !py-1.5 text-sm"
                  onClick={async () => {
                    try {
                      await getToken(); // öffnet bei Bedarf das Microsoft-Anmeldefenster
                      setState({ phase: "idle" });
                    } catch (err) {
                      setState({ phase: "idle", banner: humanError(err) });
                    }
                  }}
                >
                  Neu anmelden
                </button>
              )}
            </div>
          )}
          <SourcePicker
            onSharePointSubmit={handleSharePoint}
            onLocalPick={handleLocal}
            onSinglePick={handleSingle}
            sharePointEnabled={sharePointEnabled}
          />
        </>
      )}

      {state.phase === "loading" && (
        <div className="card text-center text-kampmeyer-neutral-500 py-12">{state.message}</div>
      )}

      {state.phase === "ready" && (
        <>
          <FileList
            folderName={state.folderName}
            files={state.files}
            mode={state.mode}
            showFiles={false}
          />
          {diag && state.mode === "local" && (
            <details
              className="rounded-md border border-kampmeyer-neutral-200 bg-kampmeyer-neutral-50 px-4 py-3 text-sm"
              open={state.files.length === 0 || diag.unreadable.length > 0}
            >
              <summary className="cursor-pointer select-none font-medium">
                Diagnose: was der Browser im Ordner sieht
              </summary>
              <ul className="mt-2 space-y-1 text-kampmeyer-neutral-600 list-disc pl-5">
                <li>{diag.totalEntries} Einträge gescannt</li>
                <li>
                  {diag.subfolderCount} Unterordner
                  {diag.sampleSubfolders.length > 0 && ` (z. B. ${diag.sampleSubfolders.join(", ")})`}
                </li>
                <li>
                  {diag.fileCount} Dateien, davon {diag.imageCount} Bilder (jpg/jpeg/png/webp/heic)
                  {diag.sampleOtherFiles.length > 0 && ` · andere: ${diag.sampleOtherFiles.join(", ")}`}
                </li>
                {diag.unreadable.length > 0 && (
                  <li className="text-red-700">
                    {diag.unreadable.length} Bilddatei(en) nicht lesbar — z. B. „{diag.unreadable[0].name}“: {diag.unreadable[0].error}. Typisch bei nur-online OneDrive-Dateien oder durch eine Browser-Richtlinie auf verwalteten Geräten.
                  </li>
                )}
                {diag.imageCount === 0 && diag.subfolderCount > 0 && (
                  <li className="text-amber-700">
                    Es wurden nur Unterordner gefunden — liegen die Bilder in einem Unterordner? Dann bitte direkt diesen auswählen.
                  </li>
                )}
              </ul>
            </details>
          )}
          {gridFiles.length > 0 && (
            <ImageCropGrid
              files={gridFiles}
              onChange={setImageCrops}
              names={imageNames}
              finalNames={finalNames}
              onNameChange={setImageName}
              aiSuggestions={rooms.state.suggestions}
              aiPending={rooms.state.pending}
              aiStatus={aiStatusText(rooms.state)}
              onPreview={rooms.onPreview}
            />
          )}
          <TitleImagePicker
            onChange={setTitles}
            onBusyChange={setTitlesBusy}
            galleryFiles={gridFiles}
            downloadMode={state.mode === "single"}
          />
          <div className="flex items-center justify-end gap-3">
            {state.files.length > 0 && titlesBusy && (
              <span className="text-sm text-kampmeyer-neutral-500 mr-auto max-w-sm">
                Bitte den Ausschnitt des Titelbilds übernehmen.
              </span>
            )}
            <button onClick={reset} className="btn-secondary shrink-0 whitespace-nowrap">
              Abbrechen
            </button>
            <button
              onClick={handleStart}
              className="btn-primary shrink-0 whitespace-nowrap"
              disabled={state.files.length === 0 || titlesBusy}
            >
              {titles.alternative
                ? "Skalieren & Titelbilder exportieren"
                : titles.primary
                  ? "Skalieren & Titelbild exportieren"
                  : "Skalieren starten"}
            </button>
          </div>
        </>
      )}

      {state.phase === "processing" && <ProgressView items={state.progress} />}

      {state.phase === "done" && (
        <ResultView
          successCount={state.successCount}
          errors={state.errors}
          durationSeconds={state.durationSeconds}
          downloaded={state.downloaded}
          onReset={reset}
        />
      )}
    </div>
  );
}

// Budget für die reinen JPEG-Daten: 1 MB minus das EXIF, das danach angehängt wird
// (plus kleine Reserve), damit die fertige Datei sicher unter 1 MB bleibt.
function aiStatusText(s: RoomSuggestionState): string | null {
  if (!s.enabled) return null;
  if (s.error) return s.error;
  if (s.total === 0) return null;
  if (s.done < s.total) return `✨ KI erkennt die Räume … ${s.done} von ${s.total}`;
  const n = Object.keys(s.suggestions).length;
  return `✨ KI hat ${n} ${n === 1 ? "Raum" : "Räume"} erkannt – bitte kurz prüfen und bei Bedarf überschreiben.`;
}

function jpegBudget(exifDict: Parameters<typeof exifOverheadBytes>[0]): number {
  return MAX_FILE_BYTES - exifOverheadBytes(exifDict, DEFAULT_OPTIONS.dpi) - 256;
}

function downloadBytes(name: string, data: ArrayBuffer): void {
  const blob = new Blob([data], { type: "image/jpeg" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Anmeldefehler nie als Microsoft-Fehlercode anzeigen, sondern verständlich mit Lösung.
const AUTH_MESSAGE = "Deine Microsoft-Anmeldung muss kurz erneuert werden. Bitte auf „Neu anmelden“ klicken.";

function humanError(err: unknown): string {
  if (err instanceof AuthError) {
    console.warn("Anmeldefehler:", err.errorCode, err.message);
    return AUTH_MESSAGE;
  }
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Unbekannter Fehler";
}
